import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { RotateCcw, Trash2 } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";
import { api, messageErreur } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { Vide } from "@/components/commun";

export default function Corbeille() {
  const { user } = useAuth();
  const [liste, setListe] = useState([]);
  const [cible, setCible] = useState(null);
  const charger = useCallback(() => api.get("/corbeille").then((r) => setListe(r.data)), []);
  useEffect(() => { charger(); }, [charger]);

  const restaurer = async (i) => {
    try { const { data } = await api.post(`/corbeille/${i.collection}/${i.id}/restaurer`); toast.success(data.message); charger(); }
    catch (e) { toast.error(messageErreur(e)); }
  };
  const definitif = async () => {
    try { const { data } = await api.delete(`/corbeille/${cible.collection}/${cible.id}`, { params: { confirmation: true } }); toast.success(data.message); charger(); }
    catch (e) { toast.error(messageErreur(e)); } finally { setCible(null); }
  };

  return (
    <div>
      <h2 className="text-base md:text-lg font-bold">Corbeille</h2>
      <p className="at-muted text-sm mt-1 mb-4">Les éléments supprimés restent restaurables pendant 30 jours.</p>
      {liste.length === 0 && <Vide texte="La corbeille est vide." testId="trash-empty" />}
      <div className="space-y-2" data-testid="trash-list">
        {liste.map((i) => (
          <div key={i.id} className="at-fiche p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid={`trash-item-${i.id}`}>
            <div className="min-w-0">
              <p className="font-bold truncate">{i.nom}</p>
              <p className="text-sm at-muted">{i.type} · supprimé le {formatDate(i.deleted_at)} par {i.deleted_by_nom} · <span data-testid={`trash-days-${i.id}`}>{i.jours_restants} jours restants</span></p>
            </div>
            <div className="flex gap-2 shrink-0">
              <button type="button" className="at-btn at-btn-primary flex-1" onClick={() => restaurer(i)} data-testid={`trash-restore-${i.id}`}><RotateCcw size={16} />Restaurer</button>
              {user.role === "dirigeant" && <button type="button" className="at-icon-btn at-btn-ghost" aria-label="Supprimer définitivement" onClick={() => setCible(i)} data-testid={`trash-delete-${i.id}`}><Trash2 size={17} /></button>}
            </div>
          </div>
        ))}
      </div>
      <AlertDialog open={Boolean(cible)} onOpenChange={(o) => !o && setCible(null)}>
        <AlertDialogContent className="at-popover max-w-[calc(100vw-24px)] sm:max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer définitivement ?</AlertDialogTitle>
            <AlertDialogDescription className="at-muted">« {cible?.nom} » sera effacé sans retour possible. Cette action est enregistrée dans l'historique.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel className="at-btn at-btn-ghost border-0" data-testid="trash-delete-cancel">Annuler</AlertDialogCancel>
            <AlertDialogAction className="at-btn" style={{ background: "var(--danger)", color: "var(--accent-ink)" }} onClick={definitif} data-testid="trash-delete-confirm">Supprimer définitivement</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
